import { useState, useEffect, useRef, useCallback } from "react";
import { MagnifyingGlass, Faders, X, Tray, PaperPlaneTilt, FileText, Archive, Trash, WarningOctagon, Folder, EnvelopeSimpleOpen } from "@phosphor-icons/react";
import dayjs from "dayjs";
import api from "@/lib/api";
import { t } from "@/lib/i18n";

const SYS = ["inbox", "sent", "drafts", "archive", "trash", "spam"];
const ICONS = { inbox: Tray, sent: PaperPlaneTilt, drafts: FileText, archive: Archive, trash: Trash, spam: WarningOctagon };

const folderLabel = (g) => g.folder_name || t(`gs.f_${g.folder}`, g.folder.charAt(0).toUpperCase() + g.folder.slice(1));

export const GlobalSearch = ({ onOpenHit }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showAdv, setShowAdv] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sysF, setSysF] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [siSel, setSiSel] = useState([]);
  const [fromF, setFromF] = useState("");
  const [hasAtt, setHasAtt] = useState(false);
  const [customFolders, setCustomFolders] = useState([]);
  const [sis, setSis] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/folders").then((r) => setCustomFolders(r.data || [])).catch(() => {});
    api.get("/shared-inboxes").then((r) => setSis((r.data.shared_inboxes || []).filter((s) => s.active))).catch(() => {});
  }, []);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setShowAdv(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtersActive = sysF.length > 0 || folderId || siSel.length > 0 || fromF || hasAtt;

  const runSearch = useCallback(async (query) => {
    if (query.trim().length < 2 && !fromF && !hasAtt) { setResult(null); return; }
    setLoading(true);
    try {
      const params = { q: query };
      if (sysF.length) params.folders = sysF.join(",");
      if (folderId) params.folder_id = folderId;
      if (siSel.length) params.mailboxes = siSel.join(",");
      if (fromF) params.from = fromF;
      if (hasAtt) params.has_attachment = true;
      const { data } = await api.get("/search", { params });
      setResult(data);
    } catch { setResult(null); }
    setLoading(false);
  }, [sysF, folderId, siSel, fromF, hasAtt]);

  useEffect(() => {
    const tm = setTimeout(() => { if (open) runSearch(q); }, 300);
    return () => clearTimeout(tm);
  }, [q, runSearch, open]);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const clearAll = () => { setSysF([]); setFolderId(""); setSiSel([]); setFromF(""); setHasAtt(false); };

  return (
    <div className="relative" ref={boxRef} data-testid="global-search-box">
      <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input data-testid="email-search-input" value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { if (q || filtersActive) setOpen(true); }}
        placeholder={t("gs.placeholder", "Search all mail & shared inboxes…")}
        className="w-full border border-gray-300 rounded-md pl-9 pr-16 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        {q && (
          <button data-testid="gs-clear-btn" onClick={() => { setQ(""); setResult(null); setOpen(false); }}
            className="text-gray-400 hover:text-gray-600 p-0.5"><X size={13} /></button>
        )}
        <button data-testid="gs-adv-btn" onClick={() => { setShowAdv(!showAdv); setOpen(true); }}
          className={`relative p-0.5 transition-colors ${showAdv || filtersActive ? "text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
          title={t("gs.advanced", "Advanced filters")}>
          <Faders size={15} />
          {filtersActive && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-blue-600" />}
        </button>
      </div>

      {showAdv && (
        <div data-testid="gs-adv-popover" className="absolute z-40 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl p-3 text-sm space-y-2.5">
          <div>
            <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">{t("gs.folders", "Folders")}</div>
            <div className="flex flex-wrap gap-1">
              {SYS.map((f) => (
                <button key={f} data-testid={`gs-adv-folder-${f}`} onClick={() => toggle(sysF, setSysF, f)}
                  className={`text-[11px] font-semibold rounded-full px-2.5 py-1 transition-colors ${sysF.includes(f) ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {t(`gs.f_${f}`, f.charAt(0).toUpperCase() + f.slice(1))}
                </button>
              ))}
            </div>
          </div>
          {customFolders.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-gray-500 uppercase">{t("gs.custom_folder", "Folder")}</span>
              <select data-testid="gs-adv-custom-folder" value={folderId} onChange={(e) => setFolderId(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-xs">
                <option value="">{t("gs.any_folder", "Any")}</option>
                {customFolders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          )}
          {sis.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1">{t("gs.shared", "Shared inboxes")}</div>
              <div className="flex flex-wrap gap-1">
                <button data-testid="gs-adv-mb-personal" onClick={() => toggle(siSel, setSiSel, "personal")}
                  className={`text-[11px] font-semibold rounded-full px-2.5 py-1 transition-colors ${siSel.includes("personal") ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {t("gs.my_mail", "My mail")}
                </button>
                {sis.map((s) => (
                  <button key={s.id} data-testid={`gs-adv-si-${s.id}`} onClick={() => toggle(siSel, setSiSel, s.id)}
                    className={`text-[11px] font-semibold rounded-full px-2.5 py-1 transition-colors ${siSel.includes(s.id) ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                    📥 {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 flex-1">
              <span className="text-[11px] font-semibold text-gray-500 uppercase">{t("gs.from", "From")}</span>
              <input data-testid="gs-adv-from-input" value={fromF} onChange={(e) => setFromF(e.target.value)}
                placeholder={t("gs.from_ph", "name or email")} className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-xs" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input data-testid="gs-adv-attach-checkbox" type="checkbox" checked={hasAtt} onChange={(e) => setHasAtt(e.target.checked)} />
              {t("gs.has_attach", "Has attachment")}
            </label>
          </div>
          <div className="flex justify-between pt-1 border-t border-gray-100">
            <button data-testid="gs-adv-clear" onClick={clearAll} className="text-xs text-gray-500 hover:text-gray-800">{t("gs.clear", "Clear filters")}</button>
            <button data-testid="gs-adv-apply" onClick={() => { setShowAdv(false); runSearch(q); setOpen(true); }}
              className="text-xs font-semibold bg-blue-600 text-white rounded-md px-3 py-1 hover:bg-blue-700">{t("gs.apply", "Apply")}</button>
          </div>
        </div>
      )}

      {open && !showAdv && result && (
        <div data-testid="global-search-panel" className="absolute z-40 top-full mt-1 left-0 right-0 max-h-[60vh] overflow-y-auto scroll-thin bg-white border border-gray-200 rounded-lg shadow-xl">
          <div className="px-3 py-2 text-[11px] text-gray-400 border-b border-gray-100 flex justify-between">
            <span data-testid="gs-results-summary">{t("gs.results_for", "Results for")} “{result.q}” — {result.total}</span>
            {loading && <span>…</span>}
          </div>
          {result.groups.length === 0 && (
            <div data-testid="gs-no-results" className="px-3 py-6 text-center text-sm text-gray-400">{t("gs.no_results", "No results found")}</div>
          )}
          {result.groups.map((g) => {
            const Icon = g.mailbox ? EnvelopeSimpleOpen : (ICONS[g.folder] || Folder);
            return (
              <div key={`${g.mailbox}-${g.folder}`} data-testid={`gs-group-${g.mailbox || "personal"}-${g.folder}`}>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-[11px] font-semibold text-gray-600 sticky top-0">
                  <Icon size={13} className={g.mailbox ? "text-amber-600" : "text-gray-400"} />
                  {g.mailbox ? <span className="text-amber-700">{g.mailbox_name} › {folderLabel(g)}</span> : folderLabel(g)}
                  <span className="text-gray-400 font-normal">({g.count})</span>
                </div>
                {g.hits.map((h) => (
                  <button key={h.id} data-testid={`gs-hit-${h.thread_id}`}
                    onClick={() => { setOpen(false); onOpenHit({ mailbox: g.mailbox, mailboxName: g.mailbox_name, folder: g.folder, folderLabel: folderLabel(g), threadId: h.thread_id }); }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-50 transition-colors">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs truncate ${h.is_read ? "text-gray-600" : "font-semibold text-gray-900"}`}>{h.from_name || h.from_email}</span>
                      <span className="ml-auto text-[10px] text-gray-400 shrink-0">{dayjs(h.created_at).format("MMM D")}</span>
                    </div>
                    <div className="text-[13px] text-gray-800 truncate">{h.subject}</div>
                    <div className="text-xs text-gray-400 truncate">{h.snippet}</div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
